import '../styles/pages.scss';
import {useEffect, useRef, useState} from "react";
import {useLocation} from 'react-router-dom';
import {
    getFirstBar,
    getGapPercentage,
    getSecondBar,
    getSelectedSecurities,
    getStrategies, getTradeData
} from "../lib/beCall";
import {Box, Flex, Spinner, Center, Text} from "@chakra-ui/react";
import Toolbar from "../components/toolbar/toolbar";
import StrategySidebar from "../components/strategySidebar/strategySidebar";
import StrategyContent from "../components/strategyContent/strategyContent";
import OfflineScreen from "../components/offlineScreen/offlineScreen";
import {useDate} from "../contexts/DateContext";


const Dashboard = () => {
    const location = useLocation();
    const {
        selectedDate: lastDay,
        setSelectedDate: setLastDay,
        isPolling,
        setIsPolling,
        selectedStrategy: savedStrategy,
        setSelectedStrategy: setSavedStrategy
    } = useDate();
    const [strategies, setStrategies] = useState([]);
    const [selectedStrategy, setSelectedStrategy] = useState(0);
    const refIndex = useRef(0);
    const [stepIndex, setStepIndex] = useState(0);
    const [stepsData, setStepsData] = useState([]);
    const [selectedTags, setSelectedTags] = useState({});
    const [isOffline, setIsOffline] = useState(false);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [nextRetryIn, setNextRetryIn] = useState(0);
    const retryTimeoutRef = useRef(null);
    const countdownIntervalRef = useRef(null);

    useEffect(() => {
        loadInitialData();
    }, []);

    const loadInitialData = async () => {
        try {
            const data = await getStrategies();
            if (data) {
                // Setta la data SOLO se non è già presente nel Context
                if (!lastDay) {
                    setLastDay(data['date']);
                }
                const strategiesList = data['strategies'];
                setStrategies(strategiesList);

                // Determina quale strategia selezionare
                if (strategiesList && strategiesList.length > 0) {
                    if (savedStrategy !== null) {
                        // Se c'è una strategia salvata in sessionStorage, usa quella
                        const strategyName = savedStrategy;
                        const strategyIndex = strategiesList.indexOf(strategyName);
                        if (strategyIndex !== -1) {
                            setSelectedStrategy(strategyIndex);
                        } else {
                            // Se la strategia salvata non esiste più, seleziona la prima alfabetica
                            const sortedStrategies = [...strategiesList].sort((a, b) =>
                                a.toLowerCase().localeCompare(b.toLowerCase())
                            );
                            const firstAlphabetic = sortedStrategies[0];
                            const indexInOriginal = strategiesList.indexOf(firstAlphabetic);
                            setSelectedStrategy(indexInOriginal);
                            setSavedStrategy(firstAlphabetic);
                        }
                    } else {
                        // Prima volta: seleziona la prima in ordine alfabetico
                        const sortedStrategies = [...strategiesList].sort((a, b) =>
                            a.toLowerCase().localeCompare(b.toLowerCase())
                        );
                        const firstAlphabetic = sortedStrategies[0];
                        const indexInOriginal = strategiesList.indexOf(firstAlphabetic);
                        setSelectedStrategy(indexInOriginal);
                        setSavedStrategy(firstAlphabetic);
                    }
                }

                setIsOffline(false);
            } else {
                handleOffline();
            }
        } catch (error) {
            console.error('Failed to load initial data:', error);
            handleOffline();
        }
    };

    const handleOffline = () => {
        setIsOffline(true);
        scheduleRetry();
    };

    const handleOnline = () => {
        setIsOffline(false);
        clearRetryTimeout();
    };

    const scheduleRetry = () => {
        clearRetryTimeout();

        // Countdown da 30 secondi
        setNextRetryIn(30);
        countdownIntervalRef.current = setInterval(() => {
            setNextRetryIn(prev => {
                if (prev <= 1) {
                    clearInterval(countdownIntervalRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        // Riprova dopo 30 secondi
        retryTimeoutRef.current = setTimeout(() => {
            loadInitialData();
        }, 30000);
    };

    const clearRetryTimeout = () => {
        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
        }
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
        setNextRetryIn(0);
    };

    const handleManualRetry = () => {
        clearRetryTimeout();
        loadInitialData();
    };

    const intervalRef = useRef(null);
    const isPollingRef = useRef(isPolling);
    const abortControllerRef = useRef(null);

    // Mantieni isPollingRef sincronizzato con isPolling
    useEffect(() => {
        isPollingRef.current = isPolling;
    }, [isPolling]);

    useEffect(() => {
        const clearCurrentInterval = () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };

        const abortCurrentRequests = () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
                abortControllerRef.current = null;
            }
        };

        // Pulisci sempre l'interval precedente e cancella le richieste
        clearCurrentInterval();
        abortCurrentRequests();

        if (!lastDay || strategies.length === 0) return;

        setStepsData([]);
        refIndex.current = 0;
        setIsLoadingData(true);

        const strategy = strategies[selectedStrategy];

        const pollingFn = async () => {
            // Crea un nuovo AbortController per questo ciclo di polling
            abortControllerRef.current = new AbortController();
            const signal = abortControllerRef.current.signal;

            try {
                let message = '';
                let table = [];
                if (refIndex.current < 2) {
                    message = await getSelectedSecurities({strategy, day: lastDay}, signal);
                    table = await getGapPercentage({strategy, day: lastDay}, signal);
                if (table && table.length > 0) {
                    setStepsData(prev => {
                        const updated = [...prev];
                        updated[0] = {
                            message: JSON.parse(JSON.stringify(message)),
                            table: table.map(item => ({...item}))
                        };
                        return updated;
                    });
                    if (refIndex.current < 1) {
                        refIndex.current = 1;
                        setStepIndex(1);
                    }
                }
            }

                if (refIndex.current < 3) {
                    table = await getFirstBar({strategy, day: lastDay}, signal);
                    if (table && table.length > 0) {
                        setStepsData(prev => {
                            const updated = [...prev];
                            updated[1] = {
                                message: {securities: [...new Set(table.map(item => item.security))]},
                                table: table.map(item => ({...item}))
                            };
                            return updated;
                        });
                        if (refIndex.current < 2) {
                            refIndex.current = 2;
                            setStepIndex(2);
                        }
                    }


                    table = await getSecondBar({strategy, day: lastDay}, signal);
                    if (table && table.length > 0) {
                        setStepsData(prev => {
                            const updated = [...prev];
                            updated[2] = {
                                message: {securities: [...new Set(table.map(item => item.security))]},
                                table: table.map(item => ({...item}))
                            };
                            return updated;
                        });
                        if (refIndex.current < 3) {
                            refIndex.current = 3;
                            setStepIndex(3);
                        }
                    }
                }

                // if (refIndex.current < 4) {
                table = await getTradeData({strategy, day: lastDay}, signal);
                if (table && table.length > 0) {
                    setStepsData(prev => {
                        const updated = [...prev];
                        updated[3] = {
                            message: {securities: [...new Set(table.map(item => item.security))]},
                            table: table.map(item => ({...item}))
                        };
                        return updated;
                    });
                    if (refIndex.current < 4) {
                        refIndex.current = 4;
                        setStepIndex(4);
                    }
                }
                // }

                // Dati caricati con successo
                setIsLoadingData(false);
                handleOnline();
            } catch (error) {
                if (error.name === 'CanceledError' || error.name === 'AbortError') {
                    console.log('Polling requests canceled');
                } else {
                    console.error('Error in polling:', error);
                    setIsLoadingData(false);
                    // Se c'è un errore durante il polling, vai offline
                    handleOffline();
                }
            }
        };

        // Carica i dati almeno una volta (anche se polling è disabilitato)
        pollingFn().then(() => {
            // Avvia il polling continuo SOLO se isPolling è true E siamo sulla dashboard
            if (isPollingRef.current && !intervalRef.current && location.pathname === '/') {
                intervalRef.current = setInterval(pollingFn, 3000);
            }
        }).catch((error) => {
            // Gestisci errori dalla prima chiamata di pollingFn
            if (error.name !== 'CanceledError' && error.name !== 'AbortError') {
                console.error('Error in initial polling:', error);
            }
        });

        return () => {
            clearCurrentInterval();
            abortCurrentRequests();
        };
    }, [lastDay, strategies, selectedStrategy, isPolling, location.pathname]);

    // Cleanup dei timeout quando il componente si smonta
    useEffect(() => {
        return () => {
            clearRetryTimeout();
        };
    }, []);

    // Wrapper per setSelectedStrategy che salva anche nel Context
    const handleStrategyChange = (index) => {
        setSelectedStrategy(index);
        if (strategies && strategies[index]) {
            setSavedStrategy(strategies[index]);
        }
    };

    // Se il sistema è offline, mostra la schermata offline
    if (isOffline) {
        return <OfflineScreen onRetry={handleManualRetry} nextRetryIn={nextRetryIn}/>;
    }

    return (
        <Flex direction="column" h="100vh" overflow="hidden">
            <Box>
                <Toolbar last_day={lastDay} setDate={setLastDay}/>
            </Box>
            <Flex flex="1" overflow="hidden">
                <StrategySidebar
                    strategies={strategies}
                    selectedStrategy={selectedStrategy}
                    setSelectedStrategy={handleStrategyChange}
                />
                <Box flex="1" overflow="auto">
                    {isLoadingData ? (
                        <Center h="100%">
                            <Flex direction="column" align="center" gap={4}>
                                <Spinner
                                    thickness="4px"
                                    speed="0.65s"
                                    emptyColor="gray.200"
                                    color="#38A169"
                                    size="xl"
                                />
                                <Text color="gray.600" fontSize="md">Caricamento dati in corso...</Text>
                            </Flex>
                        </Center>
                    ) : (
                        <StrategyContent
                            strategies={strategies}
                            selectedStrategy={selectedStrategy}
                            stepIndex={stepIndex}
                            stepsData={stepsData}
                            selectedTags={selectedTags}
                            setSelectedTags={setSelectedTags}
                        />
                    )}
                </Box>
            </Flex>
        </Flex>
    );
};

export default Dashboard;